import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { PDFFont, PDFDocument, PDFPage, rgb } from 'pdf-lib';
import { firstValueFrom } from 'rxjs';

export interface StudentDocumentData {
  firstName: string;
  lastName: string;
  pesel: string;
  pkk: string;
  phone: string;
  /** Registered address - optional, printed on the agreement. */
  street?: string;
  postalCode?: string;
  city?: string;
}

type StudentDocumentType = 'agreement' | 'rodo';

const TEMPLATE_URLS: Record<StudentDocumentType, string> = {
  agreement: 'assets/pdf-templates/umowa-regulamin-03-2026.pdf',
  rodo: 'assets/pdf-templates/zgoda-rodo-osk.pdf',
};

const FONT_URL = 'assets/pdf-templates/LiberationSans-Regular.ttf';

@Injectable({ providedIn: 'root' })
export class StudentDocumentsPdfService {
  private readonly http = inject(HttpClient);
  private readonly templatePromises: Partial<Record<StudentDocumentType, Promise<ArrayBuffer>>> = {};
  private fontPromise?: Promise<ArrayBuffer>;

  async downloadAgreement(student: StudentDocumentData): Promise<void> {
    const bytes = await this.generateAgreement(student);
    this.triggerDownload(bytes, this.fileName('umowa', student));
  }

  async downloadRodo(student: StudentDocumentData): Promise<void> {
    const bytes = await this.generateRodo(student);
    this.triggerDownload(bytes, this.fileName('zgoda-rodo', student));
  }

  async generateAgreement(student: StudentDocumentData): Promise<Uint8Array> {
    const { pdfDoc, font } = await this.loadDocument('agreement');
    const page = pdfDoc.getPages()[0];
    const fullName = this.fullName(student);

    this.drawField(page, font, this.currentDate(), 363, 779, 207, 14, 10);
    this.drawField(page, font, fullName, 101, 690, 171, 15, 10);
    this.drawField(page, font, student.pesel, 181, 671, 95, 15, 10);
    this.drawField(page, font, student.pkk, 41, 652, 229, 15, 10);
    // "Adres zameldowania": the street on the labelled line, postal code and city on the line below.
    this.drawField(page, font, student.street ?? '', 129, 633, 141, 15, 10);
    this.drawField(page, font, this.postalCodeAndCity(student), 14, 615, 256, 15, 10);
    this.drawField(page, font, this.normalizePhone(student.phone), 56, 596, 215, 15, 10);

    return pdfDoc.save();
  }

  async generateRodo(student: StudentDocumentData): Promise<Uint8Array> {
    const { pdfDoc, font } = await this.loadDocument('rodo');
    const page = pdfDoc.getPages()[0];
    const date = this.currentDate();
    const fullName = this.fullName(student);

    // A4 portrait template with a single copy: dotted lines for the date and
    // the student's full name sit above their labels, the signature stays empty.
    this.drawField(page, font, date, 71, 345, 105, 15, 13);
    this.drawField(page, font, fullName, 306, 347, 255, 15, 13);

    return pdfDoc.save();
  }

  private async loadDocument(type: StudentDocumentType): Promise<{ pdfDoc: PDFDocument; font: PDFFont }> {
    const [templateBytes, fontBytes, fontkitModule] = await Promise.all([
      this.loadTemplate(type),
      this.loadFont(),
      import('@pdf-lib/fontkit'),
    ]);
    const pdfDoc = await PDFDocument.load(templateBytes);
    pdfDoc.registerFontkit(fontkitModule.default);
    const font = await pdfDoc.embedFont(fontBytes, { subset: true });
    return { pdfDoc, font };
  }

  private loadTemplate(type: StudentDocumentType): Promise<ArrayBuffer> {
    if (!this.templatePromises[type]) {
      this.templatePromises[type] = firstValueFrom(
        this.http.get(TEMPLATE_URLS[type], { responseType: 'arraybuffer' })
      );
    }
    return this.templatePromises[type]!;
  }

  private loadFont(): Promise<ArrayBuffer> {
    if (!this.fontPromise) {
      this.fontPromise = firstValueFrom(
        this.http.get(FONT_URL, { responseType: 'arraybuffer' })
      );
    }
    return this.fontPromise;
  }

  private drawField(
    page: PDFPage,
    font: PDFFont,
    value: string,
    x: number,
    y: number,
    width: number,
    height: number,
    preferredFontSize: number
  ): void {
    const text = value.trim();
    if (!text) return;

    page.drawRectangle({ x, y, width, height, color: rgb(1, 1, 1) });

    const horizontalPadding = 3;
    let fontSize = preferredFontSize;
    while (fontSize > 7 && font.widthOfTextAtSize(text, fontSize) > width - horizontalPadding * 2) {
      fontSize -= 0.5;
    }

    const textWidth = font.widthOfTextAtSize(text, fontSize);
    page.drawText(text, {
      x: x + Math.max(horizontalPadding, (width - textWidth) / 2),
      y: y + (height - fontSize) / 2 + 1,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
  }

  private currentDate(): string {
    return new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date());
  }

  private fullName(student: StudentDocumentData): string {
    return `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim();
  }

  private postalCodeAndCity(student: StudentDocumentData): string {
    // The masked input may leave placeholder underscores, like the phone number.
    const postalCode = (student.postalCode ?? '').replace(/_/g, '');
    return `${postalCode} ${student.city ?? ''}`.trim();
  }

  private normalizePhone(phone: string): string {
    return (phone ?? '').replace(/_/g, '').trim();
  }

  private fileName(prefix: string, student: StudentDocumentData): string {
    const person = this.fullName(student)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
    return `${prefix}-${person || 'kursant'}.pdf`;
  }

  private triggerDownload(bytes: Uint8Array, fileName: string): void {
    const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
